import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, UserPlus, FolderPlus, BellRing, Mail } from "lucide-react";
import { http, formatDetail } from "../../portal/api";

const input = "field-dark px-3 py-2.5 text-sm";

export default function PortalAdminPanel() {
  const [orgs, setOrgs] = useState([]);
  const [clients, setClients] = useState([]);
  const [engs, setEngs] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", organisation_id: "" });
  const [lastInvite, setLastInvite] = useState(null);
  const [newEng, setNewEng] = useState({ title: "", organisation_id: "" });

  const load = useCallback(async () => {
    try {
      const [o, c, e, a] = await Promise.all([
        http.get("/portal/admin/organisations"), http.get("/portal/admin/clients"),
        http.get("/portal/admin/engagements"), http.get("/portal/admin/alerts"),
      ]);
      setOrgs(o.data); setClients(c.data); setEngs(e.data); setAlerts(a.data);
    } catch { toast.error("Could not load portal data."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    try {
      await http.post("/portal/admin/organisations", { name: orgName });
      toast.success("Organisation created.");
      setOrgName(""); load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post("/portal/admin/clients/invite", invite);
      setLastInvite(data);
      toast.success(data.email_sent ? "Invitation email sent." : "Invite created — email failed, share the link below.");
      setInvite({ name: "", email: "", organisation_id: "" }); load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const createEng = async (e) => {
    e.preventDefault();
    try {
      await http.post("/portal/admin/engagements", newEng);
      toast.success("Engagement created.");
      setNewEng({ title: "", organisation_id: "" }); load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const toggleAccess = async (c) => {
    try {
      const desired = c.status === "revoked" ? "active" : "revoked";
      const { data } = await http.patch(`/portal/admin/clients/${encodeURIComponent(c.email)}/access`, { status: desired });
      toast.success(`Access now: ${data.status}`);
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="portal-admin-panel" className="space-y-10">
      <div className="flex justify-end">
        <button
          data-testid="send-digest-btn"
          onClick={async () => {
            try {
              const { data } = await http.post("/portal/admin/digest/send");
              toast.success(`Digest emailed: ${data.briefings} briefings, ${data.evidence} evidence items, ${data.alerts} alerts.`);
            } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
          }}
          className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-2"
        >
          <Mail size={13} /> Email digest now
        </button>
      </div>
      {alerts.length > 0 && (
        <div className="border border-ochre/60 bg-surface1 p-5" data-testid="lifecycle-alerts">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ochre mb-3 flex items-center gap-2"><BellRing size={14} /> Data-lifecycle alerts (next 30 days)</p>
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <p key={i} data-testid={`alert-${i}`} className={`text-sm ${a.overdue ? "text-ember" : "text-stone2"}`}>
                {a.overdue ? "OVERDUE — " : ""}{a.type}: <span className="text-bone">{a.title}</span> · due {a.due_date}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={createOrg} className="border border-line bg-surface1 p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-4 flex items-center gap-2"><Building2 size={14} /> New organisation</p>
          <input data-testid="org-name-input" className={`${input} w-full mb-3`} placeholder="Organisation name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          <button type="submit" data-testid="create-org-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Create</button>
        </form>

        <form onSubmit={sendInvite} className="border border-line bg-surface1 p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-4 flex items-center gap-2"><UserPlus size={14} /> Invite client (emailed set-password link)</p>
          <input data-testid="invite-name-input" className={`${input} w-full mb-2`} placeholder="Full name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
          <input data-testid="invite-email-input" className={`${input} w-full mb-2`} placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
          <select data-testid="invite-org-select" className={`${input} w-full mb-3`} value={invite.organisation_id} onChange={(e) => setInvite({ ...invite, organisation_id: e.target.value })}>
            <option value="">Organisation…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button type="submit" data-testid="send-invite-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Send invite</button>
          {lastInvite && !lastInvite.email_sent && (
            <div className="mt-3 border border-ochre/60 p-3" data-testid="invite-link-box">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre mb-1.5">Email failed — share this set-password link with {lastInvite.email}:</p>
              <p className="text-[11px] text-bone break-all select-all" data-testid="invite-link-text">{lastInvite.invite_link}</p>
              <button type="button" data-testid="copy-invite-link-btn" onClick={() => { navigator.clipboard?.writeText(lastInvite.invite_link).then(() => toast.success("Copied.")).catch(() => {}); }} className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre hover:text-ember transition-colors duration-300 mt-2">Copy link</button>
            </div>
          )}
        </form>

        <form onSubmit={createEng} className="border border-line bg-surface1 p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-4 flex items-center gap-2"><FolderPlus size={14} /> New engagement</p>
          <input data-testid="eng-title-input" className={`${input} w-full mb-2`} placeholder="Engagement title" value={newEng.title} onChange={(e) => setNewEng({ ...newEng, title: e.target.value })} />
          <select data-testid="eng-org-select" className={`${input} w-full mb-3`} value={newEng.organisation_id} onChange={(e) => setNewEng({ ...newEng, organisation_id: e.target.value })}>
            <option value="">Organisation…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button type="submit" data-testid="create-eng-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Create</button>
        </form>
      </div>

      <div>
        <h2 className="font-serif text-2xl mb-4">Engagements</h2>
        {engs === null ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone2">Loading…</p>
        ) : engs.length === 0 ? (
          <p className="text-sm text-stone2 border border-line bg-surface1 p-8">No engagements yet.</p>
        ) : (
          <div className="space-y-px bg-line/40 border border-line/40">
            {engs.map((e, i) => (
              <Link key={e.id} to={`/admin/engagements/${e.id}`} data-testid={`admin-eng-row-${i}`} className="block bg-ink p-5 md:p-6 hover:bg-surface1 transition-colors duration-300">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <h3 className="font-serif text-lg">{e.title}</h3>
                    <span className="font-mono text-xs text-stone2">{e.organisation_name}</span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-ember/40 text-ember">{e.status}</span>
                </div>
                <div className="flex gap-4 mt-2 font-mono text-[11px] text-stone2">
                  <span>Evidence: {e.evidence_count}</span>
                  <span>Outputs: {e.output_count}</span>
                  <span>{e.agreement_active ? "Agreement active" : "Agreement not active"}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif text-2xl mb-4">Client accounts</h2>
        {clients.length === 0 ? (
          <p className="text-sm text-stone2 border border-line bg-surface1 p-8">No client accounts yet.</p>
        ) : (
          <div className="space-y-px bg-line/40 border border-line/40">
            {clients.map((c, i) => (
              <div key={c.email} data-testid={`client-row-${i}`} className="bg-ink p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-serif text-lg">{c.name}</span>
                  <span className="font-mono text-xs text-stone2">{c.email}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border border-line text-stone2">{c.organisation_name}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border ${c.status === "active" ? "border-ember/50 text-ember" : c.status === "revoked" ? "border-line text-stone2 line-through" : "border-ochre text-ochre"}`}>{c.status}</span>
                </div>
                <button onClick={() => toggleAccess(c)} data-testid={`toggle-access-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2 hover:text-ember transition-colors duration-300">
                  {c.status === "revoked" ? "Reinstate" : "Revoke access"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
